import { useCallback, useEffect, useState } from "react";
import { api } from "@repo/api-client";
import type { UserBadge } from "@repo/shared-types";

interface GamificationState {
  badges: UserBadge[];
  newlyAwarded: string[];
  loading: boolean;
  checking: boolean;
  error: string;
  checkMilestones: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useGamification(): GamificationState {
  const [badges, setBadges] = useState<UserBadge[]>([]);
  const [newlyAwarded, setNewlyAwarded] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    const data = await api.listBadges();
    setBadges(data);
  }, []);

  useEffect(() => {
    refresh()
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load badges"))
      .finally(() => setLoading(false));
  }, [refresh]);

  const checkMilestones = useCallback(async () => {
    setChecking(true);
    setError("");
    setNewlyAwarded([]);
    try {
      const result = await api.checkMilestones();
      setNewlyAwarded(result.awarded_badges);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to check milestones");
    } finally {
      setChecking(false);
    }
  }, [refresh]);

  return { badges, newlyAwarded, loading, checking, error, checkMilestones, refresh };
}
